import { BG_1X, ITEM_1X, BG_BASE, ITEMS_BASE, FG_BASE, asset, TOTAL_DAYS } from './config'
import manifest from './manifest'
import preloader from './preloader'

export type SlideRefs = {
  root: HTMLDivElement
  day: number
  bg: HTMLImageElement
  itemP1: HTMLImageElement
  itemP2: HTMLImageElement
  fg: HTMLImageElement
  hydrated: boolean
}

const Slides: SlideRefs[] = []

function buildSlides(container: HTMLDivElement) {
  for (let day = 1; day <= TOTAL_DAYS; day++) {
    const slide = buildSlide(day)
    Slides.push(slide)
    container.appendChild(slide.root)
  }
}

function buildSlide(day: number): SlideRefs {
  const root = document.createElement('div')
  root.className = 'embla__slide'
  root.dataset.day = String(day)

  const mkImg = (cls: string, alt = '') => {
    const img = document.createElement('img')
    img.className = cls
    img.alt = alt
    img.decoding = 'async'
    img.loading = 'lazy'
    img.style.pointerEvents = 'none'
    return img
  }

  const bg = mkImg('mobile-container__img-bg', `Den ${day}`)
  const itemP1 = mkImg('mobile-container__img-item mobile-item--p1')
  const itemP2 = mkImg('mobile-container__img-item mobile-item--p2 invisible')
  const fg = mkImg('mobile-container__img-foreground')

  root.append(bg, itemP1, itemP2, fg)

  return { root, day, bg, itemP1, itemP2, fg, hydrated: false }
}

const bgSrc = (d: number, size: number) => asset(`${BG_BASE}/day-${d}-${size}x${size}.jpg`)

const itemSrc = (d: number, phase: 1 | 2, size: number) => asset(`${ITEMS_BASE}/day-${d}-${phase}-${size}x${size}.png`)

const fgSrc = (d: number, size: number) => asset(`${FG_BASE}/day-${d}-foreground-${size}x${size}.png`)

function assignSourcesForSlide(slide: SlideRefs) {
  const { day } = slide

  setImgSources(slide.bg, bgSrc(day, BG_1X))
  setImgSources(slide.itemP1, itemSrc(day, 1, ITEM_1X))
  setImgSources(slide.itemP2, itemSrc(day, 2, ITEM_1X))

  if (manifest.hasForeground(day)) {
    setImgSources(slide.fg, fgSrc(day, ITEM_1X))
    slide.fg.style.display = ''
  } else {
    clearImg(slide.fg)
  }

  slide.hydrated = true
}

function setImgSources(img: HTMLImageElement, src1x: string) {
  img.sizes = '100vw'
  img.src = src1x
  // img.srcset = `${src1x} 1x`
}

function clearImg(img: HTMLImageElement) {
  img.removeAttribute('src')
  img.removeAttribute('srcset')
  img.style.display = 'none'
}

function waitDecode(img: HTMLImageElement): Promise<void> {
  if (!img || img.complete) return Promise.resolve()
  return img.decode().catch((err) => {
    console.warn('Image decode failed:', err)
  })
}

async function ensureSlideLoaded(index: number) {
  const slide = Slides[index]
  if (!slide) return

  preloader.show(true)

  if (!slide.hydrated) assignSourcesForSlide(slide)

  const jobs = [waitDecode(slide.bg), waitDecode(slide.itemP1), waitDecode(slide.itemP2)]

  if (manifest.hasForeground(slide.day) && slide.fg.getAttribute('src')) {
    jobs.push(waitDecode(slide.fg))
  }

  try {
    await Promise.all(jobs)
  } catch (err) {
    console.warn('Slide load error:', err)
  }

  preloader.show(false)
}

function preloadNeighbor(index: number) {
  const slide = Slides[index]
  if (slide && !slide.hydrated) assignSourcesForSlide(slide)
}

function warmNeighborsAround(center: number) {
  const total = Slides.length
  const prev = (center - 1 + total) % total
  const next = (center + 1) % total
  preloadNeighbor(prev)
  preloadNeighbor(next)
}

export default { Slides, buildSlides, assignSourcesForSlide, ensureSlideLoaded, preloadNeighbor, warmNeighborsAround }
